import { FormEvent, useEffect } from "react"
import { Head, useForm, usePage } from "@inertiajs/react"
import { AuthShell } from "@/components/AuthShell"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import type { PageProps } from "@/types/inertia"

type Props = {
  token: string
  email: string
  expires_at: string
}

export default function InvitationAccept({ token, email, expires_at }: Props) {
  const { props } = usePage<PageProps>()
  const errors = props.errors ?? {}
  const form = useForm({ password: "", password_confirmation: "", timezone: "" })

  useEffect(() => {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (tz) form.setData("timezone", tz)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const submit = (event: FormEvent) => {
    event.preventDefault()
    form.patch(`/invitations/${token}`)
  }

  return (
    <>
      <Head title="Accept Invitation | LeadFlow">
        <meta name="description" content="Accept your LeadFlow invitation." />
        <meta property="og:title" content="Accept Invitation | LeadFlow" />
        <meta property="og:description" content="Accept your LeadFlow invitation." />
      </Head>
      <AuthShell>
        <div className="text-center">
          <h1 className="text-3xl">Accept invitation</h1>
          <p className="mt-3 text-sm text-ink-muted">This invitation is for {email} and expires {formatDate(expires_at)}.</p>
        </div>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div className="space-y-2">
            <label>Email</label>
            <Input readOnly value={email} aria-label="Invited email" />
          </div>
          <div className="space-y-2">
            <label htmlFor="password">Password</label>
            <Input id="password" type="password" autoComplete="new-password" required aria-invalid={!!errors.password} value={form.data.password} onChange={(event) => form.setData("password", event.target.value)} />
            {errors.password && <p className="text-xs text-danger-display">{errors.password}</p>}
          </div>
          <div className="space-y-2">
            <label htmlFor="password_confirmation">Confirm password</label>
            <Input id="password_confirmation" type="password" autoComplete="new-password" required aria-invalid={!!errors.password_confirmation} value={form.data.password_confirmation} onChange={(event) => form.setData("password_confirmation", event.target.value)} />
            {errors.password_confirmation && <p className="text-xs text-danger-display">{errors.password_confirmation}</p>}
          </div>
          <Button type="submit" disabled={form.processing} className="w-full">Create account</Button>
        </form>
      </AuthShell>
    </>
  )
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
}
